import { GetInferredFromRaw } from '@thinknimble/tn-models'
import { z } from 'zod'
import { guestShape } from '../guests'

export enum TraStatus {
  not_started = 'not_started',
  submitting = 'submitting',
  success = 'success',
  failed = 'failed',
}

export const surveySnapshotShape = {
  documentType: z.string().nullable().optional(),
  documentNumber: z.string().nullable().optional(),
  dateOfBirth: z.string().nullable().optional(),
  nationality: z.string().nullable().optional(),
  visitingFrom: z.string().nullable().optional(),
  residenceCity: z.string().nullable().optional(),
}

const surveyDocumentShape = { exportKey: z.string(), objectKey: z.string() }

export const surveyReviewShape = {
  surveySnapshot: z.object(surveySnapshotShape).nullable(),
  answers: z.array(z.object({ exportKey: z.string(), value: z.string().nullable() })),
  documents: z.array(z.object(surveyDocumentShape)),
}

export const guestDocumentsShape = {
  documents: z.array(z.object(surveyDocumentShape)),
}

export type SurveySnapshot = GetInferredFromRaw<typeof surveySnapshotShape>

export const complianceStatusEnum = {
  NOT_REQUIRED: 'not_required',
  SURVEY_PENDING: 'survey_pending',
  IN_REPORTING: 'in_reporting',
  REPORTED: 'reported',
} as const

// How edits to reported contact fields (name/phone) are handled. Mirrors
// ReservationGuest.EditPhase: duplicate = saving creates a new guest entry.
export const editPhaseEnum = {
  OPEN: 'open',
  DUPLICATE: 'duplicate',
  LOCKED: 'locked',
} as const

// A government filing (TRA success / SIRE submitted). `submittedAt` comes from
// the receipt and is null when none was issued.
export const guestFilingShape = {
  system: z.string(),
  confirmationCode: z.string().nullable(),
  submittedAt: z.iso.datetime({ offset: true }).nullable(),
}

export type GuestFiling = GetInferredFromRaw<typeof guestFilingShape>

export const guestDeleteModeEnum = {
  SCRUB: 'scrub',
  PERMANENT: 'permanent',
} as const

export type GuestDeleteMode = (typeof guestDeleteModeEnum)[keyof typeof guestDeleteModeEnum]

// 409 body from a guest delete whose state changed since the card loaded.
export const guestDeleteConflictShape = {
  code: z.enum(['guest_filed', 'already_scrubbed']),
  filings: z.array(z.object(guestFilingShape)),
  hasReceipts: z.boolean(),
}

export type GuestDeleteConflict = GetInferredFromRaw<typeof guestDeleteConflictShape>

export const reservationGuestShape = {
  id: z.string().uuid(),
  reservation: z.string().uuid(),
  guest: z.string().uuid(),
  guestRef: z.object(guestShape),
  formInstance: z.string().uuid().nullable(),
  isBooker: z.boolean(),
  needsSire: z.boolean(),
  needsTra: z.boolean(),
  sireSubmitted: z.boolean(),
  sireEntryConfirmationCode: z.string().nullable().optional(),
  sireExitConfirmationCode: z.string().nullable().optional(),
  traStatus: z.enum(TraStatus),
  traData: z.record(z.string(), z.unknown()).optional(),
  sireDict: z.record(z.string(), z.unknown()).optional(),
  sireData: z.array(z.array(z.record(z.string(), z.unknown()))).optional(),
  surveySnapshot: z.object(surveySnapshotShape).nullable().optional(),
  surveyCompleted: z.boolean().optional(),
  dataScrubbed: z.boolean().optional(),
  previousContact: z.record(z.string(), z.string()).nullable().optional(),
  complianceStatus: z.enum(complianceStatusEnum).optional(),
  editPhase: z.enum(editPhaseEnum).optional(),
  receiptSystems: z.array(z.string()).optional(),
  filings: z.array(z.object(guestFilingShape)).optional(),
}

export type ReservationGuest = GetInferredFromRaw<typeof reservationGuestShape>

export const guestContactShape = {
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(),
  phoneNumber: z.string().nullable(),
}
